import { useEffect, useRef, useState } from "react";

const getFullscreenElement = () =>
  document.fullscreenElement || document.webkitFullscreenElement || null;

const callAndSwallow = (fn, target) => {
  if (!fn) {
    return;
  }
  const result = fn.call(target);
  if (result && typeof result.catch === "function") {
    result.catch(() => {});
  }
};

const requestNativeFullscreen = () => {
  const el = document.documentElement;
  callAndSwallow(el.requestFullscreen || el.webkitRequestFullscreen, el);
};

// iPhone Safari has no Fullscreen API for regular elements (only <video>);
// iPad Safari does. Feature-detect so the toggle button can hide itself
// where it would just be dead weight.
export const isFullscreenSupported = () => {
  if (typeof document === "undefined") {
    return false;
  }
  const el = document.documentElement;
  return !!(el.requestFullscreen || el.webkitRequestFullscreen);
};

// Still useful elsewhere (e.g. gating the double-tap-to-edit workaround).
export const isTouchDevice = () =>
  typeof window !== "undefined" &&
  ((window.matchMedia && window.matchMedia("(pointer: coarse)").matches) ||
    navigator.maxTouchPoints > 0);

const useFullscreen = () => {
  // Source of truth is always document.fullscreenElement, read via the
  // fullscreenchange event — never a manually-set boolean that could drift
  // from reality. wantFullscreenRef below only decides whether to *attempt*
  // a recovery; it never feeds directly into what isFullscreen reports.
  const [isFullscreen, setIsFullscreen] = useState(!!getFullscreenElement());
  // True once fullscreen has dropped out from under us while we still want
  // it on, and the silent recovery attempt below didn't win it back —
  // callers can use this to show a "tap to resume" affordance, since a real
  // tap (unlike our silent retry) always carries a genuine user gesture and
  // so can actually succeed even on platforms that reject the silent one.
  const [droppedUnexpectedly, setDroppedUnexpectedly] = useState(false);
  const wantFullscreenRef = useRef(false);

  useEffect(() => {
    const handleChange = () => {
      const active = !!getFullscreenElement();
      setIsFullscreen(active);
      if (active) {
        setDroppedUnexpectedly(false);
        return;
      }
      // The browser dropped fullscreen on its own (most commonly: a text
      // field got focus and the platform forces fullscreen closed for the
      // on-screen keyboard) while we still want it on — silently try to
      // win it back. This succeeds on some browsers/versions that allow
      // re-entering fullscreen from within the same event chain; on
      // platforms that deliberately block it (iOS Safari, most mobile
      // Chrome) it fails immediately and isFullscreen simply stays an
      // accurate "false" — no fake/stuck state either way. Either way, flag
      // it as dropped so the caller can offer a real tap to get back in.
      if (wantFullscreenRef.current) {
        requestNativeFullscreen();
        setDroppedUnexpectedly(true);
      }
    };
    document.addEventListener("fullscreenchange", handleChange);
    document.addEventListener("webkitfullscreenchange", handleChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleChange);
      document.removeEventListener("webkitfullscreenchange", handleChange);
    };
  }, []);

  // Must be invoked synchronously from a real click/tap handler —
  // requestFullscreen() silently rejects if called from a timeout, promise
  // callback, or effect instead of directly inside a user gesture.
  const toggleFullscreen = () => {
    if (getFullscreenElement()) {
      wantFullscreenRef.current = false;
      setDroppedUnexpectedly(false);
      callAndSwallow(
        document.exitFullscreen || document.webkitExitFullscreen,
        document
      );
    } else {
      wantFullscreenRef.current = true;
      setDroppedUnexpectedly(false);
      requestNativeFullscreen();
    }
  };

  // Same gesture as toggleFullscreen's "enter" branch — a dedicated name so
  // callers showing a "resume fullscreen" affordance don't need to reason
  // about toggling.
  const resumeFullscreen = () => {
    wantFullscreenRef.current = true;
    setDroppedUnexpectedly(false);
    requestNativeFullscreen();
  };

  return { isFullscreen, toggleFullscreen, droppedUnexpectedly, resumeFullscreen };
};

export default useFullscreen;
